import * as THREE from 'three'
import { applyAnimationEasing, isAnimationEasing } from '@/plugins/animation/easing'
import type {
  AnimationClipData,
  AnimationEasing,
  AnimationInterpolation,
  AnimationTrackData,
  CameraAnimationTargetRef,
} from '@/shared/types/animation'

export const SEMANTIC_CAMERA_MOTION_KIND = 'share3d.camera-motion-semantic'
export const SEMANTIC_CAMERA_MOTION_VERSION = 1

export const SEMANTIC_CAMERA_MOTION_LEAF_SEMANTICS = [
  'dolly',
  'truck',
  'orbit',
  'rise-overlook',
  'spiral',
  'figure-eight',
  'sine-wave',
  'pan-zoom-scan',
  'dolly-zoom',
  'butterfly',
  'turbulence',
  'dolly-roll',
] as const

export const SEMANTIC_CAMERA_MOTION_SEMANTICS = [
  ...SEMANTIC_CAMERA_MOTION_LEAF_SEMANTICS,
  'sequence',
] as const

export type SemanticCameraMotionLeafSemantic =
  (typeof SEMANTIC_CAMERA_MOTION_LEAF_SEMANTICS)[number]
/** 兼容既有调用方的叶子语义别名。 */
export type SemanticCameraMotionSemantic = SemanticCameraMotionLeafSemantic
export type SemanticCameraMotionTemplateSemantic = (typeof SEMANTIC_CAMERA_MOTION_SEMANTICS)[number]

export type SemanticCameraMotionDollyDirection = 'in' | 'out'
export type SemanticCameraMotionTruckDirection = 'left' | 'right'
export type SemanticCameraMotionOrbitDirection = 'left' | 'right'
export type SemanticCameraMotionSpiralVerticalDirection = 'up' | 'down'
export type SemanticCameraMotionSpiralRotationDirection = 'clockwise' | 'counterclockwise'
export type SemanticCameraMotionSpiralRadiusMode = 'fixed' | 'in' | 'out'
export type SemanticCameraMotionRollDirection = 'clockwise' | 'counterclockwise'

export interface SemanticCameraMotionDollyParams {
  direction?: SemanticCameraMotionDollyDirection
  distanceScale?: number
}

export interface SemanticCameraMotionTruckParams {
  direction?: SemanticCameraMotionTruckDirection
  translationScale?: number
}

export interface SemanticCameraMotionOrbitParams {
  direction?: SemanticCameraMotionOrbitDirection
  angle?: number
  keyframeCount?: number
}

export interface SemanticCameraMotionRiseOverlookParams {
  distanceScale?: number
  heightScale?: number
  keyframeCount?: number
}

export interface SemanticCameraMotionSpiralParams {
  verticalDirection?: SemanticCameraMotionSpiralVerticalDirection
  rotationDirection?: SemanticCameraMotionSpiralRotationDirection
  radiusMode?: SemanticCameraMotionSpiralRadiusMode
  angle?: number
  radiusScale?: number
  heightScale?: number
  keyframeCount?: number
}

export interface SemanticCameraMotionFigureEightParams {
  direction?: SemanticCameraMotionOrbitDirection
  angle?: number
  pitchAmplitude?: number
  keyframeCount?: number
}

export interface SemanticCameraMotionSineWaveParams {
  direction?: SemanticCameraMotionOrbitDirection
  angle?: number
  pitchAmplitude?: number
  waveCount?: number
  keyframeCount?: number
}

export interface SemanticCameraMotionPanZoomScanParams {
  direction?: SemanticCameraMotionOrbitDirection
  distanceAmplitude?: number
  pitchAmplitude?: number
  yawAmplitude?: number
  cycleCount?: number
  keyframeCount?: number
}

export interface SemanticCameraMotionDollyZoomParams {
  direction?: SemanticCameraMotionOrbitDirection
  angle?: number
  distanceAmplitude?: number
  pulseCount?: number
  fovCompensation?: number
  keyframeCount?: number
}

export interface SemanticCameraMotionButterflyParams {
  direction?: SemanticCameraMotionOrbitDirection
  pitchAmplitude?: number
  yawAmplitude?: number
  wingCount?: number
  keyframeCount?: number
}

export interface SemanticCameraMotionTurbulenceParams {
  direction?: SemanticCameraMotionOrbitDirection
  angle?: number
  distanceAmplitude?: number
  pitchAmplitude?: number
  yawAmplitude?: number
  harmonicCount?: number
  keyframeCount?: number
}

export interface SemanticCameraMotionDollyRollParams {
  distanceScale?: number
  rollDirection?: SemanticCameraMotionRollDirection
  rollTurns?: number
  keyframeCount?: number
}

export interface SemanticCameraMotionSegmentEasing {
  first?: AnimationEasing
  last?: AnimationEasing
}

export type SemanticCameraMotionParams =
  | SemanticCameraMotionDollyParams
  | SemanticCameraMotionTruckParams
  | SemanticCameraMotionOrbitParams
  | SemanticCameraMotionRiseOverlookParams
  | SemanticCameraMotionSpiralParams
  | SemanticCameraMotionFigureEightParams
  | SemanticCameraMotionSineWaveParams
  | SemanticCameraMotionPanZoomScanParams
  | SemanticCameraMotionDollyZoomParams
  | SemanticCameraMotionButterflyParams
  | SemanticCameraMotionTurbulenceParams
  | SemanticCameraMotionDollyRollParams

interface SemanticCameraMotionTemplateBase {
  kind: typeof SEMANTIC_CAMERA_MOTION_KIND
  version: typeof SEMANTIC_CAMERA_MOTION_VERSION
  id: string
  name: string
  description?: string
  holdDuration?: number
}

export interface SemanticCameraMotionLeafTemplate extends SemanticCameraMotionTemplateBase {
  semantic: SemanticCameraMotionLeafSemantic
  params?: SemanticCameraMotionParams
  interpolation: AnimationInterpolation
  easing?: AnimationEasing
  segmentEasing?: SemanticCameraMotionSegmentEasing
}

export interface SemanticCameraMotionSphericalOffset {
  yaw?: number
  pitch?: number
  distanceScale?: number
}

export interface SemanticCameraMotionStartOffset {
  camera?: SemanticCameraMotionSphericalOffset
  target?: SemanticCameraMotionSphericalOffset
}

export interface SemanticCameraMotionSequenceStep {
  template: SemanticCameraMotionTemplate
  durationWeight?: number
  startOffset?: SemanticCameraMotionStartOffset
}

export interface SemanticCameraMotionSequenceParams {
  steps: SemanticCameraMotionSequenceStep[]
}

export interface SemanticCameraMotionSequenceTemplate extends SemanticCameraMotionTemplateBase {
  semantic: 'sequence'
  params: SemanticCameraMotionSequenceParams
  interpolation?: never
  easing?: never
  segmentEasing?: never
}

export type SemanticCameraMotionTemplate =
  | SemanticCameraMotionLeafTemplate
  | SemanticCameraMotionSequenceTemplate

export type SemanticCameraMotionVector3Like =
  | readonly [number, number, number]
  | { x: number; y: number; z: number }

export interface SemanticCameraMotionContext {
  camera: {
    position: SemanticCameraMotionVector3Like
    target: SemanticCameraMotionVector3Like
    up: SemanticCameraMotionVector3Like
    fov: number
    roll?: number
  }
}

export interface SemanticCameraMotionOptions {
  duration: number
}

export interface SemanticCameraMotionKeyframe {
  time: number
  position: [number, number, number]
  target: [number, number, number]
  fov: number
  roll: number
}

export interface SemanticCameraMotionDiagnostics {
  distance: number
  clamped: boolean
}

export interface SemanticCameraMotionResult {
  keyframes: SemanticCameraMotionKeyframe[]
  clip: AnimationClipData
  diagnostics: SemanticCameraMotionDiagnostics
}

const ALLOWED_LEAF_TEMPLATE_FIELDS = new Set([
  'kind',
  'version',
  'id',
  'name',
  'description',
  'semantic',
  'params',
  'interpolation',
  'easing',
  'segmentEasing',
  'holdDuration',
])

const ALLOWED_SEQUENCE_TEMPLATE_FIELDS = new Set([
  'kind',
  'version',
  'id',
  'name',
  'description',
  'semantic',
  'params',
  'holdDuration',
])

const ANIMATION_INTERPOLATIONS = new Set<AnimationInterpolation>(['step', 'linear', 'spline'])

const SEMANTIC_SET = new Set<string>(SEMANTIC_CAMERA_MOTION_SEMANTICS)

const MIN_DISTANCE_SCALE = 0.25
const DOLLY_IN_DISTANCE_SCALE = 0.35
const DOLLY_OUT_DISTANCE_SCALE = 1.5
const MAX_DOLLY_IN_DISTANCE_SCALE = 0.99
const TRUCK_TRANSLATION_SCALE = 0.5
const MAX_TRUCK_TRANSLATION_SCALE = 5
const DEFAULT_DOLLY_DIRECTION: SemanticCameraMotionDollyDirection = 'in'
const DEFAULT_TRUCK_DIRECTION: SemanticCameraMotionTruckDirection = 'right'
const DEFAULT_ORBIT_DIRECTION: SemanticCameraMotionOrbitDirection = 'right'
const DEFAULT_SPIRAL_VERTICAL_DIRECTION: SemanticCameraMotionSpiralVerticalDirection = 'up'
const DEFAULT_SPIRAL_ROTATION_DIRECTION: SemanticCameraMotionSpiralRotationDirection = 'clockwise'
const DEFAULT_SPIRAL_RADIUS_MODE: SemanticCameraMotionSpiralRadiusMode = 'fixed'
const DEFAULT_ROLL_DIRECTION: SemanticCameraMotionRollDirection = 'clockwise'
const RISE_DISTANCE_SCALE = 1.15
const RISE_HEIGHT_SCALE = 0.35
const ORBIT_ANGLE_DEGREES = 90
const SPIRAL_ANGLE_DEGREES = 360
const SPIRAL_HEIGHT_SCALE = 0.5
const SPIRAL_IN_RADIUS_SCALE = 0.65
const SPIRAL_OUT_RADIUS_SCALE = 1.35
const DEFAULT_ORBIT_KEYFRAME_COUNT = 3
const DEFAULT_RISE_KEYFRAME_COUNT = 3
const DEFAULT_SPIRAL_KEYFRAME_COUNT = 13
const MIN_KEYFRAME_COUNT = 2
const MAX_KEYFRAME_COUNT = 120
const MAX_ORBIT_ANGLE_DEGREES = 360
const MAX_SPIRAL_ANGLE_DEGREES = 1440
const MAX_SPIRAL_RADIUS_SCALE = 10
const MAX_SHOWCASE_ANGLE_DEGREES = 1440
const MAX_PITCH_AMPLITUDE_DEGREES = 60
const MAX_YAW_AMPLITUDE_DEGREES = 180
const MAX_DISTANCE_AMPLITUDE = 1 - MIN_DISTANCE_SCALE
const MAX_WAVE_COUNT = 12
const MAX_CYCLE_COUNT = 8
const MAX_PULSE_COUNT = 8
const MAX_WING_COUNT = 8
const MIN_HARMONIC_COUNT = 2
const MAX_HARMONIC_COUNT = 8
const MAX_ROLL_TURNS = 4
const MIN_INTERVALS_PER_OSCILLATION = 6
const MIN_INTERVALS_PER_ORBIT = 12
const MIN_DOLLY_ROLL_KEYFRAME_COUNT = 5
const TURBULENCE_PITCH_PHASE_MODULATION = 0.5
const FIGURE_EIGHT_ANGLE_DEGREES = 360
const FIGURE_EIGHT_PITCH_AMPLITUDE_DEGREES = 17.2
const FIGURE_EIGHT_KEYFRAME_COUNT = 49
const SINE_WAVE_ANGLE_DEGREES = 360
const SINE_WAVE_PITCH_AMPLITUDE_DEGREES = 5.7
const SINE_WAVE_COUNT = 6
const SINE_WAVE_KEYFRAME_COUNT = 73
const PAN_ZOOM_SCAN_DISTANCE_AMPLITUDE = 0.3
const PAN_ZOOM_SCAN_PITCH_AMPLITUDE_DEGREES = 8.6
const PAN_ZOOM_SCAN_YAW_AMPLITUDE_DEGREES = 28.6
const PAN_ZOOM_SCAN_CYCLE_COUNT = 1
const PAN_ZOOM_SCAN_KEYFRAME_COUNT = 49
const DOLLY_ZOOM_ANGLE_DEGREES = 360
const DOLLY_ZOOM_DISTANCE_AMPLITUDE = 0.4
const DOLLY_ZOOM_PULSE_COUNT = 1
const DOLLY_ZOOM_FOV_COMPENSATION = 1
const DOLLY_ZOOM_KEYFRAME_COUNT = 49
const BUTTERFLY_PITCH_AMPLITUDE_DEGREES = 11.5
const BUTTERFLY_YAW_AMPLITUDE_DEGREES = 43
const BUTTERFLY_WING_COUNT = 1
const BUTTERFLY_KEYFRAME_COUNT = 49
const TURBULENCE_ANGLE_DEGREES = 360
const TURBULENCE_DISTANCE_AMPLITUDE = 0.15
const TURBULENCE_PITCH_AMPLITUDE_DEGREES = 5.7
const TURBULENCE_YAW_AMPLITUDE_DEGREES = 8.6
const TURBULENCE_HARMONIC_COUNT = 3
const TURBULENCE_KEYFRAME_COUNT = 73
const DOLLY_ROLL_DISTANCE_SCALE = 0.5
const DOLLY_ROLL_TURNS = 1
const DOLLY_ROLL_KEYFRAME_COUNT = 25
const MIN_PERSPECTIVE_FOV = 1
const MAX_PERSPECTIVE_FOV = 179
const TWO_PI = Math.PI * 2
const MIN_ABSOLUTE_DISTANCE = 1e-4
const EPSILON = 1e-8
const ORBIT_AXIS_PARALLEL_EPSILON = 1e-6
const MAX_SEQUENCE_DEPTH = 8
const MAX_SEQUENCE_LEAF_COUNT = 64
const MAX_SEQUENCE_STEP_COUNT = 32
const MIN_SEQUENCE_DISTANCE_SCALE = 0.25
const MAX_SEQUENCE_DISTANCE_SCALE = 4
const MIN_SEQUENCE_ANGLE_DEGREES = -180
const MAX_SEQUENCE_ANGLE_DEGREES = 180
const WORLD_UP_AXIS = new THREE.Vector3(0, 0, 1)
const ENDPOINT_PROGRESS_EASING_BY_PAIR = new Map<string, AnimationEasing>([
  ['linearIn:linearOut', 'linearInOut'],
  ['quadraticIn:quadraticOut', 'quadraticInOut'],
  ['cubicIn:cubicOut', 'cubicInOut'],
  ['quarticIn:quarticOut', 'quarticInOut'],
  ['quinticIn:quinticOut', 'quinticInOut'],
  ['sineIn:sineOut', 'sineInOut'],
  ['expoIn:expoOut', 'expoInOut'],
  ['circIn:circOut', 'circInOut'],
  ['backIn:backOut', 'backInOut'],
  ['bounceIn:bounceOut', 'bounceInOut'],
  ['elasticIn:elasticOut', 'elasticInOut'],
])

interface NormalizedCameraMotionInput {
  position: THREE.Vector3
  target: THREE.Vector3
  up: THREE.Vector3
  fov: number
  roll: number
  distance: number
  duration: number
}

export function parseSemanticCameraMotionTemplate(value: unknown): SemanticCameraMotionTemplate {
  return parseSemanticCameraMotionTemplateNode(value, {
    depth: 1,
    counters: { leafCount: 0 },
    ancestors: new WeakSet<object>(),
  })
}

export function isSemanticCameraMotionSequenceTemplate(
  template: SemanticCameraMotionTemplate
): template is SemanticCameraMotionSequenceTemplate {
  return template.semantic === 'sequence'
}

export function isSemanticCameraMotionLeafTemplate(
  template: SemanticCameraMotionTemplate
): template is SemanticCameraMotionLeafTemplate {
  return template.semantic !== 'sequence'
}

interface SemanticCameraMotionParseState {
  depth: number
  counters: { leafCount: number }
  ancestors: WeakSet<object>
}

function parseSemanticCameraMotionTemplateNode(
  value: unknown,
  state: SemanticCameraMotionParseState
): SemanticCameraMotionTemplate {
  const record = expectRecord(value, 'Semantic camera motion template must be an object')

  if (state.depth > MAX_SEQUENCE_DEPTH) {
    throw new Error(`Semantic camera motion sequence depth must not exceed ${MAX_SEQUENCE_DEPTH}`)
  }
  if (state.ancestors.has(record)) {
    throw new Error('Semantic camera motion sequence must not contain a circular reference')
  }
  state.ancestors.add(record)

  try {
    return parseSemanticCameraMotionTemplateRecord(record, state)
  } finally {
    state.ancestors.delete(record)
  }
}

function parseSemanticCameraMotionTemplateRecord(
  record: Record<string, unknown>,
  state: SemanticCameraMotionParseState
): SemanticCameraMotionTemplate {
  const semantic = parseSemantic(record.semantic)
  const allowedFields =
    semantic === 'sequence' ? ALLOWED_SEQUENCE_TEMPLATE_FIELDS : ALLOWED_LEAF_TEMPLATE_FIELDS

  for (const key of Object.keys(record)) {
    if (!allowedFields.has(key)) {
      throw new Error(`Unknown semantic camera motion field: ${key}`)
    }
  }

  if (record.kind !== SEMANTIC_CAMERA_MOTION_KIND) {
    throw new Error(`Unsupported semantic camera motion kind: ${String(record.kind)}`)
  }
  if (record.version !== SEMANTIC_CAMERA_MOTION_VERSION) {
    throw new Error(`Unsupported semantic camera motion version: ${String(record.version)}`)
  }

  const id = expectNonEmptyString(record.id, 'id')
  const name = expectNonEmptyString(record.name, 'name')
  const description =
    record.description === undefined ? undefined : expectString(record.description, 'description')
  const holdDuration = parseOptionalNonNegativeNumber(record.holdDuration, 'holdDuration')

  if (semantic === 'sequence') {
    const params = parseSequenceParams(record.params, state)
    return {
      kind: SEMANTIC_CAMERA_MOTION_KIND,
      version: SEMANTIC_CAMERA_MOTION_VERSION,
      id,
      name,
      ...(description !== undefined ? { description } : {}),
      semantic,
      params,
      ...(holdDuration !== undefined ? { holdDuration } : {}),
    }
  }

  state.counters.leafCount += 1
  if (state.counters.leafCount > MAX_SEQUENCE_LEAF_COUNT) {
    throw new Error(
      `Semantic camera motion sequence leaf count must not exceed ${MAX_SEQUENCE_LEAF_COUNT}`
    )
  }

  const params = parseParams(record.params, semantic)
  const interpolation = parseInterpolation(record.interpolation)
  const easing = parseEasing(record.easing)
  const segmentEasing = parseSegmentEasing(record.segmentEasing)

  if (interpolation === 'step' && (easing !== undefined || segmentEasing !== undefined)) {
    throw new Error('Semantic camera motion easing is not supported with step interpolation')
  }

  return {
    kind: SEMANTIC_CAMERA_MOTION_KIND,
    version: SEMANTIC_CAMERA_MOTION_VERSION,
    id,
    name,
    ...(description !== undefined ? { description } : {}),
    semantic,
    ...(params !== undefined ? { params } : {}),
    interpolation,
    ...(easing !== undefined ? { easing } : {}),
    ...(segmentEasing !== undefined ? { segmentEasing } : {}),
    ...(holdDuration !== undefined ? { holdDuration } : {}),
  }
}

function parseSequenceParams(
  value: unknown,
  state: SemanticCameraMotionParseState
): SemanticCameraMotionSequenceParams {
  const record = expectRecord(value, 'Semantic camera motion sequence params must be an object')
  expectAllowedParams(record, ['steps'], 'sequence')

  if (!Array.isArray(record.steps)) {
    throw new Error('Semantic camera motion sequence params.steps must be an array')
  }
  if (record.steps.length > MAX_SEQUENCE_STEP_COUNT) {
    throw new Error(
      `Semantic camera motion sequence params.steps must not contain more than ${MAX_SEQUENCE_STEP_COUNT} steps`
    )
  }

  return {
    steps: record.steps.map((step, index) => parseSequenceStep(step, index, state)),
  }
}

function parseSequenceStep(
  value: unknown,
  index: number,
  state: SemanticCameraMotionParseState
): SemanticCameraMotionSequenceStep {
  const record = expectRecord(
    value,
    `Semantic camera motion sequence step ${index} must be an object`
  )
  expectAllowedParams(
    record,
    ['template', 'durationWeight', 'startOffset'],
    `sequence step ${index}`
  )

  if (index === 0 && record.startOffset !== undefined) {
    throw new Error('Semantic camera motion sequence first step must omit startOffset')
  }

  const durationWeight = parseOptionalPositiveNumber(
    record.durationWeight,
    `params.steps[${index}].durationWeight`
  )
  const startOffset = parseStartOffset(record.startOffset, index)
  const template = parseSemanticCameraMotionTemplateNode(record.template, {
    ...state,
    depth: state.depth + 1,
  })

  return {
    template,
    ...(durationWeight !== undefined ? { durationWeight } : {}),
    ...(startOffset !== undefined ? { startOffset } : {}),
  }
}

function parseStartOffset(
  value: unknown,
  stepIndex: number
): SemanticCameraMotionStartOffset | undefined {
  if (value === undefined) {
    return undefined
  }

  const record = expectRecord(
    value,
    `Semantic camera motion params.steps[${stepIndex}].startOffset must be an object`
  )
  expectAllowedParams(record, ['camera', 'target'], `sequence step ${stepIndex} startOffset`)
  const camera = parseSphericalOffset(record.camera, stepIndex, 'camera')
  const target = parseSphericalOffset(record.target, stepIndex, 'target')

  return {
    ...(camera !== undefined ? { camera } : {}),
    ...(target !== undefined ? { target } : {}),
  }
}

function parseSphericalOffset(
  value: unknown,
  stepIndex: number,
  field: 'camera' | 'target'
): SemanticCameraMotionSphericalOffset | undefined {
  if (value === undefined) {
    return undefined
  }

  const fieldName = `params.steps[${stepIndex}].startOffset.${field}`
  const record = expectRecord(value, `Semantic camera motion ${fieldName} must be an object`)
  expectAllowedParams(record, ['yaw', 'pitch', 'distanceScale'], fieldName)
  const yaw = parseOptionalInclusiveRangeNumber(
    record.yaw,
    `${fieldName}.yaw`,
    MIN_SEQUENCE_ANGLE_DEGREES,
    MAX_SEQUENCE_ANGLE_DEGREES
  )
  const pitch = parseOptionalInclusiveRangeNumber(
    record.pitch,
    `${fieldName}.pitch`,
    MIN_SEQUENCE_ANGLE_DEGREES,
    MAX_SEQUENCE_ANGLE_DEGREES
  )
  const distanceScale = parseOptionalInclusiveRangeNumber(
    record.distanceScale,
    `${fieldName}.distanceScale`,
    MIN_SEQUENCE_DISTANCE_SCALE,
    MAX_SEQUENCE_DISTANCE_SCALE
  )

  return compactParams({ yaw, pitch, distanceScale })
}

export function createSemanticCameraMotion(
  template: unknown,
  context: SemanticCameraMotionContext,
  options: SemanticCameraMotionOptions
): SemanticCameraMotionResult {
  const normalizedTemplate = parseSemanticCameraMotionTemplate(template)
  if (!isSemanticCameraMotionLeafTemplate(normalizedTemplate)) {
    throw new Error('createSemanticCameraMotion only accepts leaf camera motion templates')
  }
  const input = normalizeMotionInput(context, options)
  const { keyframes: motionKeyframes, clamped } = createMotionKeyframes(normalizedTemplate, input)
  const keyframes = appendHoldKeyframe(normalizedTemplate, motionKeyframes, input.duration)
  const clip = createMotionClip(
    normalizedTemplate,
    keyframes,
    getKeyframesDuration(keyframes),
    Math.max(0, motionKeyframes.length - 1)
  )

  return {
    keyframes,
    clip,
    diagnostics: {
      distance: input.distance,
      clamped,
    },
  }
}

function expectRecord(value: unknown, message: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(message)
  }
  return value as Record<string, unknown>
}

function expectString(value: unknown, fieldName: string): string {
  if (typeof value !== 'string') {
    throw new Error(`Semantic camera motion ${fieldName} must be a string`)
  }
  return value
}

function expectNonEmptyString(value: unknown, fieldName: string): string {
  const text = expectString(value, fieldName).trim()
  if (!text) {
    throw new Error(`Semantic camera motion ${fieldName} must not be empty`)
  }
  return text
}

function parseSemantic(value: unknown): SemanticCameraMotionTemplateSemantic {
  if (typeof value !== 'string' || !SEMANTIC_SET.has(value)) {
    throw new Error(`Unsupported semantic camera motion semantic: ${String(value)}`)
  }
  return value as SemanticCameraMotionTemplateSemantic
}

function parseInterpolation(value: unknown): AnimationInterpolation {
  if (typeof value !== 'string' || !ANIMATION_INTERPOLATIONS.has(value as AnimationInterpolation)) {
    throw new Error(`Unsupported semantic camera motion interpolation: ${String(value)}`)
  }
  return value as AnimationInterpolation
}

function parseEasing(value: unknown): AnimationEasing | undefined {
  if (value === undefined) {
    return undefined
  }
  if (value === 'none') {
    throw new Error('Semantic camera motion easing must be omitted instead of "none"')
  }
  if (!isAnimationEasing(value)) {
    throw new Error(`Unsupported semantic camera motion easing: ${String(value)}`)
  }
  return value
}

function parseSegmentEasing(value: unknown): SemanticCameraMotionSegmentEasing | undefined {
  if (value === undefined) {
    return undefined
  }

  const record = expectRecord(value, 'Semantic camera motion segmentEasing must be an object')
  expectAllowedParams(record, ['first', 'last'], 'segmentEasing')

  const first = parseEasing(record.first)
  const last = parseEasing(record.last)
  if (first === undefined && last === undefined) {
    throw new Error('Semantic camera motion segmentEasing must include first or last')
  }

  return {
    ...(first !== undefined ? { first } : {}),
    ...(last !== undefined ? { last } : {}),
  }
}

function parseParams(
  value: unknown,
  semantic: SemanticCameraMotionLeafSemantic
): SemanticCameraMotionParams | undefined {
  if (value === undefined) {
    return undefined
  }

  const record = expectRecord(value, 'Semantic camera motion params must be an object')

  switch (semantic) {
    case 'dolly':
      return parseDollyParams(record)
    case 'truck':
      return parseTruckParams(record)
    case 'orbit':
      return parseOrbitParams(record)
    case 'rise-overlook':
      return parseRiseOverlookParams(record)
    case 'spiral':
      return parseSpiralParams(record)
    case 'figure-eight':
      return parseFigureEightParams(record)
    case 'sine-wave':
      return parseSineWaveParams(record)
    case 'pan-zoom-scan':
      return parsePanZoomScanParams(record)
    case 'dolly-zoom':
      return parseDollyZoomParams(record)
    case 'butterfly':
      return parseButterflyParams(record)
    case 'turbulence':
      return parseTurbulenceParams(record)
    case 'dolly-roll':
      return parseDollyRollParams(record)
  }
  throw new Error(`Unsupported semantic camera motion semantic: ${String(semantic)}`)
}

function parseTruckParams(record: Record<string, unknown>): SemanticCameraMotionTruckParams {
  expectAllowedParams(record, ['direction', 'translationScale'], 'truck')
  const direction = parseOptionalTruckDirection(record.direction)
  const translationScale = parseOptionalInclusiveRangeNumber(
    record.translationScale,
    'params.translationScale',
    0,
    MAX_TRUCK_TRANSLATION_SCALE
  )

  return compactParams({ direction, translationScale })
}

function parseDollyParams(record: Record<string, unknown>): SemanticCameraMotionDollyParams {
  expectAllowedParams(record, ['direction', 'distanceScale'], 'dolly')
  const direction = parseOptionalDollyDirection(record.direction)
  const distanceScale = parseOptionalNonNegativeNumber(record.distanceScale, 'params.distanceScale')
  const effectiveDirection = direction ?? DEFAULT_DOLLY_DIRECTION

  if (
    effectiveDirection === 'in' &&
    distanceScale !== undefined &&
    distanceScale > MAX_DOLLY_IN_DISTANCE_SCALE
  ) {
    throw new Error(
      `Semantic camera motion params.distanceScale must be between 0 and ${MAX_DOLLY_IN_DISTANCE_SCALE} for dolly in`
    )
  }
  if (effectiveDirection === 'out' && distanceScale !== undefined && distanceScale <= 1) {
    throw new Error(
      'Semantic camera motion params.distanceScale must be greater than 1 for dolly out'
    )
  }

  return {
    ...(direction !== undefined ? { direction } : {}),
    ...(distanceScale !== undefined ? { distanceScale } : {}),
  }
}

function parseOrbitParams(record: Record<string, unknown>): SemanticCameraMotionOrbitParams {
  expectAllowedParams(record, ['direction', 'angle', 'keyframeCount'], 'orbit')
  const direction = parseOptionalOrbitDirection(record.direction)
  const angle = parseOptionalRangeNumber(record.angle, 'params.angle', 0, MAX_ORBIT_ANGLE_DEGREES)
  const keyframeCount = parseOptionalKeyframeCount(record.keyframeCount)

  return {
    ...(direction !== undefined ? { direction } : {}),
    ...(angle !== undefined ? { angle } : {}),
    ...(keyframeCount !== undefined ? { keyframeCount } : {}),
  }
}

function parseRiseOverlookParams(
  record: Record<string, unknown>
): SemanticCameraMotionRiseOverlookParams {
  expectAllowedParams(record, ['distanceScale', 'heightScale', 'keyframeCount'], 'rise-overlook')
  const distanceScale = parseOptionalPositiveNumber(record.distanceScale, 'params.distanceScale')
  const heightScale = parseOptionalNonNegativeNumber(record.heightScale, 'params.heightScale')
  const keyframeCount = parseOptionalKeyframeCount(record.keyframeCount)

  return {
    ...(distanceScale !== undefined ? { distanceScale } : {}),
    ...(heightScale !== undefined ? { heightScale } : {}),
    ...(keyframeCount !== undefined ? { keyframeCount } : {}),
  }
}

function parseSpiralParams(record: Record<string, unknown>): SemanticCameraMotionSpiralParams {
  expectAllowedParams(
    record,
    [
      'verticalDirection',
      'rotationDirection',
      'radiusMode',
      'angle',
      'radiusScale',
      'heightScale',
      'keyframeCount',
    ],
    'spiral'
  )
  const verticalDirection = parseOptionalSpiralVerticalDirection(record.verticalDirection)
  const rotationDirection = parseOptionalSpiralRotationDirection(record.rotationDirection)
  const radiusMode = parseOptionalSpiralRadiusMode(record.radiusMode)
  const angle = parseOptionalRangeNumber(record.angle, 'params.angle', 0, MAX_SPIRAL_ANGLE_DEGREES)
  const radiusScale = parseOptionalRangeNumber(
    record.radiusScale,
    'params.radiusScale',
    0,
    MAX_SPIRAL_RADIUS_SCALE
  )
  const heightScale = parseOptionalNonNegativeNumber(record.heightScale, 'params.heightScale')
  const keyframeCount = parseOptionalKeyframeCount(record.keyframeCount)

  validateSpiralRadiusScale(radiusMode ?? DEFAULT_SPIRAL_RADIUS_MODE, radiusScale)

  return {
    ...(verticalDirection !== undefined ? { verticalDirection } : {}),
    ...(rotationDirection !== undefined ? { rotationDirection } : {}),
    ...(radiusMode !== undefined ? { radiusMode } : {}),
    ...(angle !== undefined ? { angle } : {}),
    ...(radiusScale !== undefined ? { radiusScale } : {}),
    ...(heightScale !== undefined ? { heightScale } : {}),
    ...(keyframeCount !== undefined ? { keyframeCount } : {}),
  }
}

function parseFigureEightParams(
  record: Record<string, unknown>
): SemanticCameraMotionFigureEightParams {
  expectAllowedParams(
    record,
    ['direction', 'angle', 'pitchAmplitude', 'keyframeCount'],
    'figure-eight'
  )
  const direction = parseOptionalOrbitDirection(record.direction)
  const angle = parseOptionalRangeNumber(
    record.angle,
    'params.angle',
    0,
    MAX_SHOWCASE_ANGLE_DEGREES
  )
  const pitchAmplitude = parseOptionalInclusiveRangeNumber(
    record.pitchAmplitude,
    'params.pitchAmplitude',
    0,
    MAX_PITCH_AMPLITUDE_DEGREES
  )
  const keyframeCount = parseOptionalKeyframeCount(record.keyframeCount)

  return compactParams({ direction, angle, pitchAmplitude, keyframeCount })
}

function parseSineWaveParams(record: Record<string, unknown>): SemanticCameraMotionSineWaveParams {
  expectAllowedParams(
    record,
    ['direction', 'angle', 'pitchAmplitude', 'waveCount', 'keyframeCount'],
    'sine-wave'
  )
  const direction = parseOptionalOrbitDirection(record.direction)
  const angle = parseOptionalRangeNumber(
    record.angle,
    'params.angle',
    0,
    MAX_SHOWCASE_ANGLE_DEGREES
  )
  const pitchAmplitude = parseOptionalInclusiveRangeNumber(
    record.pitchAmplitude,
    'params.pitchAmplitude',
    0,
    MAX_PITCH_AMPLITUDE_DEGREES
  )
  const waveCount = parseOptionalIntegerRange(
    record.waveCount,
    'params.waveCount',
    1,
    MAX_WAVE_COUNT
  )
  const keyframeCount = parseOptionalKeyframeCount(record.keyframeCount)

  return compactParams({ direction, angle, pitchAmplitude, waveCount, keyframeCount })
}

function parsePanZoomScanParams(
  record: Record<string, unknown>
): SemanticCameraMotionPanZoomScanParams {
  expectAllowedParams(
    record,
    [
      'direction',
      'distanceAmplitude',
      'pitchAmplitude',
      'yawAmplitude',
      'cycleCount',
      'keyframeCount',
    ],
    'pan-zoom-scan'
  )
  const direction = parseOptionalOrbitDirection(record.direction)
  const distanceAmplitude = parseDistanceAmplitude(record.distanceAmplitude)
  const pitchAmplitude = parsePitchAmplitude(record.pitchAmplitude)
  const yawAmplitude = parseYawAmplitude(record.yawAmplitude)
  const cycleCount = parseOptionalIntegerRange(
    record.cycleCount,
    'params.cycleCount',
    1,
    MAX_CYCLE_COUNT
  )
  const keyframeCount = parseOptionalKeyframeCount(record.keyframeCount)

  return compactParams({
    direction,
    distanceAmplitude,
    pitchAmplitude,
    yawAmplitude,
    cycleCount,
    keyframeCount,
  })
}

function parseDollyZoomParams(
  record: Record<string, unknown>
): SemanticCameraMotionDollyZoomParams {
  expectAllowedParams(
    record,
    ['direction', 'angle', 'distanceAmplitude', 'pulseCount', 'fovCompensation', 'keyframeCount'],
    'dolly-zoom'
  )
  const direction = parseOptionalOrbitDirection(record.direction)
  const angle = parseOptionalInclusiveRangeNumber(
    record.angle,
    'params.angle',
    0,
    MAX_SHOWCASE_ANGLE_DEGREES
  )
  const distanceAmplitude = parseDistanceAmplitude(record.distanceAmplitude)
  const pulseCount = parseOptionalIntegerRange(
    record.pulseCount,
    'params.pulseCount',
    1,
    MAX_PULSE_COUNT
  )
  const fovCompensation = parseOptionalInclusiveRangeNumber(
    record.fovCompensation,
    'params.fovCompensation',
    0,
    1
  )
  const keyframeCount = parseOptionalKeyframeCount(record.keyframeCount)

  return compactParams({
    direction,
    angle,
    distanceAmplitude,
    pulseCount,
    fovCompensation,
    keyframeCount,
  })
}

function parseButterflyParams(
  record: Record<string, unknown>
): SemanticCameraMotionButterflyParams {
  expectAllowedParams(
    record,
    ['direction', 'pitchAmplitude', 'yawAmplitude', 'wingCount', 'keyframeCount'],
    'butterfly'
  )
  const direction = parseOptionalOrbitDirection(record.direction)
  const pitchAmplitude = parsePitchAmplitude(record.pitchAmplitude)
  const yawAmplitude = parseYawAmplitude(record.yawAmplitude)
  const wingCount = parseOptionalIntegerRange(
    record.wingCount,
    'params.wingCount',
    1,
    MAX_WING_COUNT
  )
  const keyframeCount = parseOptionalKeyframeCount(record.keyframeCount)

  return compactParams({ direction, pitchAmplitude, yawAmplitude, wingCount, keyframeCount })
}

function parseTurbulenceParams(
  record: Record<string, unknown>
): SemanticCameraMotionTurbulenceParams {
  expectAllowedParams(
    record,
    [
      'direction',
      'angle',
      'distanceAmplitude',
      'pitchAmplitude',
      'yawAmplitude',
      'harmonicCount',
      'keyframeCount',
    ],
    'turbulence'
  )
  const direction = parseOptionalOrbitDirection(record.direction)
  const angle = parseOptionalRangeNumber(
    record.angle,
    'params.angle',
    0,
    MAX_SHOWCASE_ANGLE_DEGREES
  )
  const distanceAmplitude = parseDistanceAmplitude(record.distanceAmplitude)
  const pitchAmplitude = parsePitchAmplitude(record.pitchAmplitude)
  const yawAmplitude = parseYawAmplitude(record.yawAmplitude)
  const harmonicCount = parseOptionalIntegerRange(
    record.harmonicCount,
    'params.harmonicCount',
    MIN_HARMONIC_COUNT,
    MAX_HARMONIC_COUNT
  )
  const keyframeCount = parseOptionalKeyframeCount(record.keyframeCount)

  return compactParams({
    direction,
    angle,
    distanceAmplitude,
    pitchAmplitude,
    yawAmplitude,
    harmonicCount,
    keyframeCount,
  })
}

function parseDollyRollParams(
  record: Record<string, unknown>
): SemanticCameraMotionDollyRollParams {
  expectAllowedParams(
    record,
    ['distanceScale', 'rollDirection', 'rollTurns', 'keyframeCount'],
    'dolly-roll'
  )
  const distanceScale = parseOptionalInclusiveRangeNumber(
    record.distanceScale,
    'params.distanceScale',
    MIN_DISTANCE_SCALE,
    1
  )
  const rollDirection = parseOptionalRollDirection(record.rollDirection)
  const rollTurns = parseOptionalInclusiveRangeNumber(
    record.rollTurns,
    'params.rollTurns',
    0,
    MAX_ROLL_TURNS
  )
  const keyframeCount = parseOptionalKeyframeCount(record.keyframeCount)

  return compactParams({ distanceScale, rollDirection, rollTurns, keyframeCount })
}

function compactParams<T extends Record<string, unknown>>(params: T): T {
  return Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined)) as T
}

function parseDistanceAmplitude(value: unknown): number | undefined {
  return parseOptionalInclusiveRangeNumber(
    value,
    'params.distanceAmplitude',
    0,
    MAX_DISTANCE_AMPLITUDE
  )
}

function parsePitchAmplitude(value: unknown): number | undefined {
  return parseOptionalInclusiveRangeNumber(
    value,
    'params.pitchAmplitude',
    0,
    MAX_PITCH_AMPLITUDE_DEGREES
  )
}

function parseYawAmplitude(value: unknown): number | undefined {
  return parseOptionalInclusiveRangeNumber(
    value,
    'params.yawAmplitude',
    0,
    MAX_YAW_AMPLITUDE_DEGREES
  )
}

function expectAllowedParams(
  record: Record<string, unknown>,
  allowedFields: readonly string[],
  semantic: string
): void {
  const allowed = new Set(allowedFields)
  for (const key of Object.keys(record)) {
    if (!allowed.has(key)) {
      throw new Error(`Unknown semantic camera motion params field for ${semantic}: ${key}`)
    }
  }
}

function parseOptionalDollyDirection(
  value: unknown
): SemanticCameraMotionDollyDirection | undefined {
  if (value === undefined) {
    return undefined
  }
  if (value !== 'in' && value !== 'out') {
    throw new Error(`Unsupported semantic camera motion params.direction: ${String(value)}`)
  }
  return value
}

function parseOptionalTruckDirection(
  value: unknown
): SemanticCameraMotionTruckDirection | undefined {
  if (value === undefined) {
    return undefined
  }
  if (value !== 'left' && value !== 'right') {
    throw new Error(`Unsupported semantic camera motion params.direction: ${String(value)}`)
  }
  return value
}

function parseOptionalOrbitDirection(
  value: unknown
): SemanticCameraMotionOrbitDirection | undefined {
  if (value === undefined) {
    return undefined
  }
  if (value !== 'left' && value !== 'right') {
    throw new Error(`Unsupported semantic camera motion params.direction: ${String(value)}`)
  }
  return value
}

function parseOptionalSpiralVerticalDirection(
  value: unknown
): SemanticCameraMotionSpiralVerticalDirection | undefined {
  if (value === undefined) {
    return undefined
  }
  if (value !== 'up' && value !== 'down') {
    throw new Error(`Unsupported semantic camera motion params.verticalDirection: ${String(value)}`)
  }
  return value
}

function parseOptionalSpiralRotationDirection(
  value: unknown
): SemanticCameraMotionSpiralRotationDirection | undefined {
  if (value === undefined) {
    return undefined
  }
  if (value !== 'clockwise' && value !== 'counterclockwise') {
    throw new Error(`Unsupported semantic camera motion params.rotationDirection: ${String(value)}`)
  }
  return value
}

function parseOptionalSpiralRadiusMode(
  value: unknown
): SemanticCameraMotionSpiralRadiusMode | undefined {
  if (value === undefined) {
    return undefined
  }
  if (value !== 'fixed' && value !== 'in' && value !== 'out') {
    throw new Error(`Unsupported semantic camera motion params.radiusMode: ${String(value)}`)
  }
  return value
}

function parseOptionalRollDirection(value: unknown): SemanticCameraMotionRollDirection | undefined {
  if (value === undefined) {
    return undefined
  }
  if (value !== 'clockwise' && value !== 'counterclockwise') {
    throw new Error(`Unsupported semantic camera motion params.rollDirection: ${String(value)}`)
  }
  return value
}

function validateSpiralRadiusScale(
  radiusMode: SemanticCameraMotionSpiralRadiusMode,
  radiusScale: number | undefined
): void {
  if (radiusScale === undefined) {
    return
  }

  if (radiusMode === 'in' && radiusScale >= 1) {
    throw new Error('Semantic camera motion params.radiusScale must be less than 1 for spiral in')
  }
  if (radiusMode === 'out' && radiusScale <= 1) {
    throw new Error(
      'Semantic camera motion params.radiusScale must be greater than 1 for spiral out'
    )
  }
  if (radiusMode === 'fixed' && radiusScale !== 1) {
    throw new Error('Semantic camera motion params.radiusScale must be 1 for fixed spiral radius')
  }
}

function parseOptionalPositiveNumber(value: unknown, fieldName: string): number | undefined {
  if (value === undefined) {
    return undefined
  }
  const numberValue = readFiniteNumber(value, fieldName)
  if (numberValue <= 0) {
    throw new Error(`Semantic camera motion ${fieldName} must be greater than 0`)
  }
  return numberValue
}

function parseOptionalNonNegativeNumber(value: unknown, fieldName: string): number | undefined {
  if (value === undefined) {
    return undefined
  }
  const numberValue = readFiniteNumber(value, fieldName)
  if (numberValue < 0) {
    throw new Error(`Semantic camera motion ${fieldName} must be greater than or equal to 0`)
  }
  return numberValue
}

function parseOptionalRangeNumber(
  value: unknown,
  fieldName: string,
  minExclusive: number,
  maxInclusive: number
): number | undefined {
  if (value === undefined) {
    return undefined
  }
  const numberValue = readFiniteNumber(value, fieldName)
  if (numberValue <= minExclusive || numberValue > maxInclusive) {
    throw new Error(
      `Semantic camera motion ${fieldName} must be greater than ${minExclusive} ` +
        `and less than or equal to ${maxInclusive}`
    )
  }
  return numberValue
}

function parseOptionalInclusiveRangeNumber(
  value: unknown,
  fieldName: string,
  minInclusive: number,
  maxInclusive: number
): number | undefined {
  if (value === undefined) {
    return undefined
  }
  const numberValue = readFiniteNumber(value, fieldName)
  if (numberValue < minInclusive || numberValue > maxInclusive) {
    throw new Error(
      `Semantic camera motion ${fieldName} must be between ${minInclusive} and ${maxInclusive}`
    )
  }
  return numberValue
}

function parseOptionalIntegerRange(
  value: unknown,
  fieldName: string,
  minInclusive: number,
  maxInclusive: number
): number | undefined {
  if (value === undefined) {
    return undefined
  }
  if (typeof value !== 'number' || !Number.isInteger(value)) {
    throw new Error(`Semantic camera motion ${fieldName} must be an integer`)
  }
  if (value < minInclusive || value > maxInclusive) {
    throw new Error(
      `Semantic camera motion ${fieldName} must be between ${minInclusive} and ${maxInclusive}`
    )
  }
  return value
}

function parseOptionalKeyframeCount(value: unknown): number | undefined {
  return parseOptionalIntegerRange(
    value,
    'params.keyframeCount',
    MIN_KEYFRAME_COUNT,
    MAX_KEYFRAME_COUNT
  )
}

function normalizeMotionInput(
  context: SemanticCameraMotionContext,
  options: SemanticCameraMotionOptions
): NormalizedCameraMotionInput {
  const contextRecord = expectRecord(context, 'Semantic camera motion context must be an object')
  const camera = expectRecord(
    contextRecord.camera,
    'Semantic camera motion context.camera must be an object'
  )
  const optionsRecord = expectRecord(options, 'Semantic camera motion options must be an object')
  const duration = readFiniteNumber(optionsRecord.duration, 'duration')
  if (!Number.isFinite(duration) || duration <= 0) {
    throw new Error('Semantic camera motion duration must be a positive finite number')
  }

  const position = toVector3(camera.position, 'camera.position')
  const target = toVector3(camera.target, 'camera.target')
  const up = toVector3(camera.up, 'camera.up')
  const fov = readFiniteNumber(camera.fov, 'camera.fov')
  const roll = camera.roll === undefined ? 0 : readFiniteNumber(camera.roll, 'camera.roll')

  if (!Number.isFinite(fov) || fov <= 0) {
    throw new Error('Semantic camera motion camera.fov must be a positive finite number')
  }
  if (!Number.isFinite(roll)) {
    throw new Error('Semantic camera motion camera.roll must be finite when provided')
  }
  if (up.lengthSq() <= EPSILON) {
    throw new Error('Semantic camera motion camera.up must not be a zero vector')
  }

  const distance = position.distanceTo(target)
  if (!Number.isFinite(distance) || distance <= EPSILON) {
    throw new Error('Semantic camera motion camera.position must not equal camera.target')
  }

  return {
    position,
    target,
    up: up.normalize(),
    fov,
    roll,
    distance,
    duration,
  }
}

function readFiniteNumber(value: unknown, fieldName: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`Semantic camera motion ${fieldName} must be a finite number`)
  }
  return value
}

function toVector3(value: unknown, fieldName: string): THREE.Vector3 {
  const candidate = Array.isArray(value)
    ? tupleToVector3(value, fieldName)
    : objectToVector3(value, fieldName)

  if (
    !Number.isFinite(candidate.x) ||
    !Number.isFinite(candidate.y) ||
    !Number.isFinite(candidate.z)
  ) {
    throw new Error(`Semantic camera motion ${fieldName} must contain finite coordinates`)
  }

  return candidate
}

function tupleToVector3(value: readonly unknown[], fieldName: string): THREE.Vector3 {
  if (value.length !== 3) {
    throw new Error(`Semantic camera motion ${fieldName} must contain exactly 3 coordinates`)
  }

  return new THREE.Vector3(
    readFiniteNumber(value[0], `${fieldName}.x`),
    readFiniteNumber(value[1], `${fieldName}.y`),
    readFiniteNumber(value[2], `${fieldName}.z`)
  )
}

function objectToVector3(value: unknown, fieldName: string): THREE.Vector3 {
  const record = expectRecord(
    value,
    `Semantic camera motion ${fieldName} must be a vector tuple or object`
  )

  return new THREE.Vector3(
    readFiniteNumber(record.x, `${fieldName}.x`),
    readFiniteNumber(record.y, `${fieldName}.y`),
    readFiniteNumber(record.z, `${fieldName}.z`)
  )
}

function createMotionKeyframes(
  template: SemanticCameraMotionLeafTemplate,
  input: NormalizedCameraMotionInput
): { keyframes: SemanticCameraMotionKeyframe[]; clamped: boolean } {
  switch (template.semantic) {
    case 'dolly': {
      const params = resolveDollyParams(template.params)
      return createDollyKeyframes(input, params.distanceScale)
    }
    case 'truck':
      return createTruckKeyframes(input, resolveTruckParams(template.params))
    case 'orbit': {
      const params = resolveOrbitParams(template.params)
      const signedAngle = params.direction === 'left' ? params.angle : -params.angle
      return createOrbitKeyframes(input, signedAngle, params.keyframeCount)
    }
    case 'rise-overlook':
      return createRiseOverlookKeyframes(input, resolveRiseOverlookParams(template.params))
    case 'spiral':
      return createSpiralKeyframes(
        input,
        resolveSpiralParams(template.params),
        resolveEndpointProgressEasing(template.segmentEasing)
      )
    case 'figure-eight':
      return createFigureEightKeyframes(input, resolveFigureEightParams(template.params))
    case 'sine-wave':
      return createSineWaveKeyframes(input, resolveSineWaveParams(template.params))
    case 'pan-zoom-scan':
      return createPanZoomScanKeyframes(input, resolvePanZoomScanParams(template.params))
    case 'dolly-zoom':
      return createDollyZoomKeyframes(input, resolveDollyZoomParams(template.params))
    case 'butterfly':
      return createButterflyKeyframes(input, resolveButterflyParams(template.params))
    case 'turbulence':
      return createTurbulenceKeyframes(input, resolveTurbulenceParams(template.params))
    case 'dolly-roll':
      return createDollyRollKeyframes(input, resolveDollyRollParams(template.params))
  }
  throw new Error(`Unsupported semantic camera motion semantic: ${String(template.semantic)}`)
}

function resolveTruckParams(
  params: SemanticCameraMotionParams | undefined
): Required<SemanticCameraMotionTruckParams> {
  const truckParams = (params ?? {}) as SemanticCameraMotionTruckParams
  return {
    direction: truckParams.direction ?? DEFAULT_TRUCK_DIRECTION,
    translationScale: truckParams.translationScale ?? TRUCK_TRANSLATION_SCALE,
  }
}

function resolveDollyParams(params: SemanticCameraMotionParams | undefined): {
  direction: SemanticCameraMotionDollyDirection
  distanceScale: number
} {
  const dollyParams = (params ?? {}) as SemanticCameraMotionDollyParams
  const direction = dollyParams.direction ?? DEFAULT_DOLLY_DIRECTION
  const distanceScale =
    dollyParams.distanceScale ??
    (direction === 'in' ? DOLLY_IN_DISTANCE_SCALE : DOLLY_OUT_DISTANCE_SCALE)

  return {
    direction,
    distanceScale,
  }
}

function resolveOrbitParams(params: SemanticCameraMotionParams | undefined): {
  direction: SemanticCameraMotionOrbitDirection
  angle: number
  keyframeCount: number
} {
  const orbitParams = (params ?? {}) as SemanticCameraMotionOrbitParams
  return {
    direction: orbitParams.direction ?? DEFAULT_ORBIT_DIRECTION,
    angle: THREE.MathUtils.degToRad(orbitParams.angle ?? ORBIT_ANGLE_DEGREES),
    keyframeCount: orbitParams.keyframeCount ?? DEFAULT_ORBIT_KEYFRAME_COUNT,
  }
}

function resolveRiseOverlookParams(
  params: SemanticCameraMotionParams | undefined
): Required<SemanticCameraMotionRiseOverlookParams> {
  const riseParams = (params ?? {}) as SemanticCameraMotionRiseOverlookParams
  return {
    distanceScale: riseParams.distanceScale ?? RISE_DISTANCE_SCALE,
    heightScale: riseParams.heightScale ?? RISE_HEIGHT_SCALE,
    keyframeCount: riseParams.keyframeCount ?? DEFAULT_RISE_KEYFRAME_COUNT,
  }
}

function resolveSpiralParams(
  params: SemanticCameraMotionParams | undefined
): Required<SemanticCameraMotionSpiralParams> {
  const spiralParams = (params ?? {}) as SemanticCameraMotionSpiralParams
  const radiusMode = spiralParams.radiusMode ?? DEFAULT_SPIRAL_RADIUS_MODE

  return {
    verticalDirection: spiralParams.verticalDirection ?? DEFAULT_SPIRAL_VERTICAL_DIRECTION,
    rotationDirection: spiralParams.rotationDirection ?? DEFAULT_SPIRAL_ROTATION_DIRECTION,
    radiusMode,
    angle: spiralParams.angle ?? SPIRAL_ANGLE_DEGREES,
    radiusScale: spiralParams.radiusScale ?? getDefaultSpiralRadiusScale(radiusMode),
    heightScale: spiralParams.heightScale ?? SPIRAL_HEIGHT_SCALE,
    keyframeCount: spiralParams.keyframeCount ?? DEFAULT_SPIRAL_KEYFRAME_COUNT,
  }
}

function resolveFigureEightParams(
  params: SemanticCameraMotionParams | undefined
): Required<SemanticCameraMotionFigureEightParams> {
  const values = (params ?? {}) as SemanticCameraMotionFigureEightParams
  const angle = values.angle ?? FIGURE_EIGHT_ANGLE_DEGREES
  return {
    direction: values.direction ?? DEFAULT_ORBIT_DIRECTION,
    angle,
    pitchAmplitude: values.pitchAmplitude ?? FIGURE_EIGHT_PITCH_AMPLITUDE_DEGREES,
    keyframeCount: resolveShowcaseKeyframeCount({
      requested: values.keyframeCount,
      fallback: FIGURE_EIGHT_KEYFRAME_COUNT,
      oscillationCount: 2,
      angle,
    }),
  }
}

function resolveSineWaveParams(
  params: SemanticCameraMotionParams | undefined
): Required<SemanticCameraMotionSineWaveParams> {
  const values = (params ?? {}) as SemanticCameraMotionSineWaveParams
  const angle = values.angle ?? SINE_WAVE_ANGLE_DEGREES
  const waveCount = values.waveCount ?? SINE_WAVE_COUNT
  return {
    direction: values.direction ?? DEFAULT_ORBIT_DIRECTION,
    angle,
    pitchAmplitude: values.pitchAmplitude ?? SINE_WAVE_PITCH_AMPLITUDE_DEGREES,
    waveCount,
    keyframeCount: resolveShowcaseKeyframeCount({
      requested: values.keyframeCount,
      fallback: SINE_WAVE_KEYFRAME_COUNT,
      oscillationCount: waveCount,
      angle,
    }),
  }
}

function resolvePanZoomScanParams(
  params: SemanticCameraMotionParams | undefined
): Required<SemanticCameraMotionPanZoomScanParams> {
  const values = (params ?? {}) as SemanticCameraMotionPanZoomScanParams
  const cycleCount = values.cycleCount ?? PAN_ZOOM_SCAN_CYCLE_COUNT
  return {
    direction: values.direction ?? DEFAULT_ORBIT_DIRECTION,
    distanceAmplitude: values.distanceAmplitude ?? PAN_ZOOM_SCAN_DISTANCE_AMPLITUDE,
    pitchAmplitude: values.pitchAmplitude ?? PAN_ZOOM_SCAN_PITCH_AMPLITUDE_DEGREES,
    yawAmplitude: values.yawAmplitude ?? PAN_ZOOM_SCAN_YAW_AMPLITUDE_DEGREES,
    cycleCount,
    keyframeCount: resolveShowcaseKeyframeCount({
      requested: values.keyframeCount,
      fallback: PAN_ZOOM_SCAN_KEYFRAME_COUNT,
      oscillationCount: cycleCount * 2,
    }),
  }
}

function resolveDollyZoomParams(
  params: SemanticCameraMotionParams | undefined
): Required<SemanticCameraMotionDollyZoomParams> {
  const values = (params ?? {}) as SemanticCameraMotionDollyZoomParams
  const angle = values.angle ?? DOLLY_ZOOM_ANGLE_DEGREES
  const pulseCount = values.pulseCount ?? DOLLY_ZOOM_PULSE_COUNT
  return {
    direction: values.direction ?? DEFAULT_ORBIT_DIRECTION,
    angle,
    distanceAmplitude: values.distanceAmplitude ?? DOLLY_ZOOM_DISTANCE_AMPLITUDE,
    pulseCount,
    fovCompensation: values.fovCompensation ?? DOLLY_ZOOM_FOV_COMPENSATION,
    keyframeCount: resolveShowcaseKeyframeCount({
      requested: values.keyframeCount,
      fallback: DOLLY_ZOOM_KEYFRAME_COUNT,
      oscillationCount: pulseCount,
      angle,
    }),
  }
}

function resolveButterflyParams(
  params: SemanticCameraMotionParams | undefined
): Required<SemanticCameraMotionButterflyParams> {
  const values = (params ?? {}) as SemanticCameraMotionButterflyParams
  const wingCount = values.wingCount ?? BUTTERFLY_WING_COUNT
  return {
    direction: values.direction ?? DEFAULT_ORBIT_DIRECTION,
    pitchAmplitude: values.pitchAmplitude ?? BUTTERFLY_PITCH_AMPLITUDE_DEGREES,
    yawAmplitude: values.yawAmplitude ?? BUTTERFLY_YAW_AMPLITUDE_DEGREES,
    wingCount,
    keyframeCount: resolveShowcaseKeyframeCount({
      requested: values.keyframeCount,
      fallback: BUTTERFLY_KEYFRAME_COUNT,
      oscillationCount: wingCount * 2,
    }),
  }
}

function resolveTurbulenceParams(
  params: SemanticCameraMotionParams | undefined
): Required<SemanticCameraMotionTurbulenceParams> {
  const values = (params ?? {}) as SemanticCameraMotionTurbulenceParams
  const angle = values.angle ?? TURBULENCE_ANGLE_DEGREES
  const harmonicCount = values.harmonicCount ?? TURBULENCE_HARMONIC_COUNT
  return {
    direction: values.direction ?? DEFAULT_ORBIT_DIRECTION,
    angle,
    distanceAmplitude: values.distanceAmplitude ?? TURBULENCE_DISTANCE_AMPLITUDE,
    pitchAmplitude: values.pitchAmplitude ?? TURBULENCE_PITCH_AMPLITUDE_DEGREES,
    yawAmplitude: values.yawAmplitude ?? TURBULENCE_YAW_AMPLITUDE_DEGREES,
    harmonicCount,
    keyframeCount: resolveShowcaseKeyframeCount({
      requested: values.keyframeCount,
      fallback: TURBULENCE_KEYFRAME_COUNT,
      oscillationCount: harmonicCount,
      angle,
    }),
  }
}

function resolveDollyRollParams(
  params: SemanticCameraMotionParams | undefined
): Required<SemanticCameraMotionDollyRollParams> {
  const values = (params ?? {}) as SemanticCameraMotionDollyRollParams
  return {
    distanceScale: values.distanceScale ?? DOLLY_ROLL_DISTANCE_SCALE,
    rollDirection: values.rollDirection ?? DEFAULT_ROLL_DIRECTION,
    rollTurns: values.rollTurns ?? DOLLY_ROLL_TURNS,
    keyframeCount: resolveShowcaseKeyframeCount({
      requested: values.keyframeCount,
      fallback: DOLLY_ROLL_KEYFRAME_COUNT,
      minimum: MIN_DOLLY_ROLL_KEYFRAME_COUNT,
    }),
  }
}

interface ShowcaseKeyframeCountOptions {
  requested?: number
  fallback: number
  oscillationCount?: number
  angle?: number
  minimum?: number
}

function resolveShowcaseKeyframeCount(options: ShowcaseKeyframeCountOptions): number {
  const oscillationIntervals = Math.ceil(
    (options.oscillationCount ?? 0) * MIN_INTERVALS_PER_OSCILLATION
  )
  const orbitIntervals = Math.ceil(((options.angle ?? 0) / 360) * MIN_INTERVALS_PER_ORBIT)
  const safeCount = Math.max(
    options.minimum ?? MIN_KEYFRAME_COUNT,
    oscillationIntervals + 1,
    orbitIntervals + 1
  )

  return Math.min(MAX_KEYFRAME_COUNT, Math.max(options.requested ?? options.fallback, safeCount))
}

function getDefaultSpiralRadiusScale(radiusMode: SemanticCameraMotionSpiralRadiusMode): number {
  switch (radiusMode) {
    case 'in':
      return SPIRAL_IN_RADIUS_SCALE
    case 'out':
      return SPIRAL_OUT_RADIUS_SCALE
    case 'fixed':
      return 1
  }
}

function createDollyKeyframes(
  input: NormalizedCameraMotionInput,
  distanceScale: number
): { keyframes: SemanticCameraMotionKeyframe[]; clamped: boolean } {
  const direction = input.position.clone().sub(input.target).normalize()
  const minDistance = Math.min(input.distance, MIN_ABSOLUTE_DISTANCE)
  const unclampedDistance = input.distance * distanceScale
  const endDistance = Math.max(minDistance, unclampedDistance)
  const endPosition = input.target.clone().addScaledVector(direction, endDistance)

  return {
    keyframes: [
      createKeyframe(0, input.position, input),
      createKeyframe(input.duration, endPosition, input),
    ],
    clamped: endDistance !== unclampedDistance,
  }
}

function createTruckKeyframes(
  input: NormalizedCameraMotionInput,
  params: Required<SemanticCameraMotionTruckParams>
): { keyframes: SemanticCameraMotionKeyframe[]; clamped: boolean } {
  const viewDirection = input.target.clone().sub(input.position).normalize()
  const lateralAxis = resolveTruckLateralAxis(viewDirection, input.up)
  const directionSign = params.direction === 'right' ? 1 : -1
  const translation = lateralAxis.multiplyScalar(
    input.distance * params.translationScale * directionSign
  )
  const endPosition = input.position.clone().add(translation)
  const endTarget = input.target.clone().add(translation)

  return {
    keyframes: [
      createKeyframe(0, input.position, input),
      createKeyframe(input.duration, endPosition, input, { target: endTarget }),
    ],
    clamped: false,
  }
}

function resolveTruckLateralAxis(viewDirection: THREE.Vector3, up: THREE.Vector3): THREE.Vector3 {
  const lateralAxis = viewDirection.clone().cross(up)
  if (lateralAxis.lengthSq() > ORBIT_AXIS_PARALLEL_EPSILON) {
    return lateralAxis.normalize()
  }

  return createFallbackOrbitAxis(viewDirection)
}

function createOrbitKeyframes(
  input: NormalizedCameraMotionInput,
  totalAngle: number,
  count: number
): { keyframes: SemanticCameraMotionKeyframe[]; clamped: boolean } {
  const offset = input.position.clone().sub(input.target)
  const axis = resolveOrbitAxis(offset, input.up)
  const keyframes: SemanticCameraMotionKeyframe[] = []

  for (let index = 0; index < count; index++) {
    const progress = count === 1 ? 1 : index / (count - 1)
    const position = input.target
      .clone()
      .add(offset.clone().applyAxisAngle(axis, totalAngle * progress))
    keyframes.push(createKeyframe(input.duration * progress, position, input))
  }

  return {
    keyframes,
    clamped: false,
  }
}

function resolveOrbitAxis(offset: THREE.Vector3, up: THREE.Vector3): THREE.Vector3 {
  const direction = offset.clone().normalize()
  const axis = up.clone().normalize()
  const crossLengthSq = direction.clone().cross(axis).lengthSq()

  if (crossLengthSq > ORBIT_AXIS_PARALLEL_EPSILON) {
    return axis
  }

  return createFallbackOrbitAxis(direction)
}

function createFallbackOrbitAxis(direction: THREE.Vector3): THREE.Vector3 {
  const candidates = [
    new THREE.Vector3(0, 0, 1),
    new THREE.Vector3(0, 1, 0),
    new THREE.Vector3(1, 0, 0),
  ]

  for (const candidate of candidates) {
    const axis = direction.clone().cross(candidate)
    if (axis.lengthSq() > EPSILON) {
      return axis.normalize()
    }
  }

  return new THREE.Vector3(1, 0, 0)
}

function createRiseOverlookKeyframes(
  input: NormalizedCameraMotionInput,
  params: Required<SemanticCameraMotionRiseOverlookParams>
): { keyframes: SemanticCameraMotionKeyframe[]; clamped: boolean } {
  const direction = input.position.clone().sub(input.target).normalize()
  const minDistance = getMinDistance(input.distance)
  const riseOffset = input.distance * params.heightScale
  const keyframes: SemanticCameraMotionKeyframe[] = []
  let clamped = false

  for (let index = 0; index < params.keyframeCount; index++) {
    const progress = params.keyframeCount === 1 ? 1 : index / (params.keyframeCount - 1)
    const distanceScale = THREE.MathUtils.lerp(1, params.distanceScale, progress)
    const unclampedDistance = input.distance * distanceScale
    const distance = Math.max(minDistance, unclampedDistance)
    const position = input.target
      .clone()
      .addScaledVector(direction, distance)
      .addScaledVector(input.up, riseOffset * progress)

    clamped = clamped || distance !== unclampedDistance
    keyframes.push(createKeyframe(input.duration * progress, position, input))
  }

  return {
    keyframes,
    clamped,
  }
}

function createSpiralKeyframes(
  input: NormalizedCameraMotionInput,
  params: Required<SemanticCameraMotionSpiralParams>,
  progressEasing: AnimationEasing | undefined
): { keyframes: SemanticCameraMotionKeyframe[]; clamped: boolean } {
  const axis = WORLD_UP_AXIS.clone()
  const offset = input.position.clone().sub(input.target)
  const startHeight = offset.dot(axis)
  const radialOffset = offset.clone().addScaledVector(axis, -startHeight)
  const radialLength = radialOffset.length()
  const radialDirection =
    radialLength > EPSILON ? radialOffset.normalize() : createPerpendicularVector(axis)
  const rotationSign = params.rotationDirection === 'counterclockwise' ? 1 : -1
  const verticalSign = params.verticalDirection === 'up' ? 1 : -1
  const totalAngle = THREE.MathUtils.degToRad(params.angle) * rotationSign
  const endRadius = radialLength * params.radiusScale
  const heightDelta = Math.abs(startHeight) * params.heightScale * verticalSign
  const keyframes: SemanticCameraMotionKeyframe[] = []

  for (let index = 0; index < params.keyframeCount; index++) {
    const rawProgress = params.keyframeCount === 1 ? 1 : index / (params.keyframeCount - 1)
    const progress = applyEndpointProgressEasing(rawProgress, progressEasing)
    const radius = THREE.MathUtils.lerp(radialLength, endRadius, progress)
    const height = startHeight + heightDelta * progress
    const radial = radialDirection
      .clone()
      .multiplyScalar(radius)
      .applyAxisAngle(axis, totalAngle * progress)
    const position = input.target.clone().add(radial).addScaledVector(axis, height)

    keyframes.push(createKeyframe(input.duration * rawProgress, position, input))
  }

  return {
    keyframes,
    clamped: false,
  }
}

interface SphericalMotionSample {
  yaw: number
  pitch: number
  distanceScale?: number
  fov?: number
  roll?: number
  clamped?: boolean
}

function createFigureEightKeyframes(
  input: NormalizedCameraMotionInput,
  params: Required<SemanticCameraMotionFigureEightParams>
): { keyframes: SemanticCameraMotionKeyframe[]; clamped: boolean } {
  const totalYaw = getSignedYaw(params.direction, params.angle)
  const pitchAmplitude = THREE.MathUtils.degToRad(params.pitchAmplitude)

  return createSphericalMotionKeyframes(input, params.keyframeCount, (progress) => ({
    yaw: totalYaw * progress,
    pitch: Math.sin(TWO_PI * 2 * progress) * pitchAmplitude,
  }))
}

function createSineWaveKeyframes(
  input: NormalizedCameraMotionInput,
  params: Required<SemanticCameraMotionSineWaveParams>
): { keyframes: SemanticCameraMotionKeyframe[]; clamped: boolean } {
  const totalYaw = getSignedYaw(params.direction, params.angle)
  const pitchAmplitude = THREE.MathUtils.degToRad(params.pitchAmplitude)

  return createSphericalMotionKeyframes(input, params.keyframeCount, (progress) => ({
    yaw: totalYaw * progress,
    pitch: Math.sin(TWO_PI * params.waveCount * progress) * pitchAmplitude,
  }))
}

function createPanZoomScanKeyframes(
  input: NormalizedCameraMotionInput,
  params: Required<SemanticCameraMotionPanZoomScanParams>
): { keyframes: SemanticCameraMotionKeyframe[]; clamped: boolean } {
  const directionSign = getOrbitDirectionSign(params.direction)
  const pitchAmplitude = THREE.MathUtils.degToRad(params.pitchAmplitude)
  const yawAmplitude = THREE.MathUtils.degToRad(params.yawAmplitude)

  return createSphericalMotionKeyframes(input, params.keyframeCount, (progress) => {
    const phase = TWO_PI * params.cycleCount * progress
    return {
      yaw: Math.sin(phase) * yawAmplitude * directionSign,
      pitch: Math.sin(phase * 2) * pitchAmplitude,
      distanceScale: 1 - Math.sin(phase) * params.distanceAmplitude,
    }
  })
}

function createDollyZoomKeyframes(
  input: NormalizedCameraMotionInput,
  params: Required<SemanticCameraMotionDollyZoomParams>
): { keyframes: SemanticCameraMotionKeyframe[]; clamped: boolean } {
  const totalYaw = getSignedYaw(params.direction, params.angle)
  const minDistanceScale = getMinDistance(input.distance) / input.distance

  return createSphericalMotionKeyframes(input, params.keyframeCount, (progress) => {
    const requestedDistanceScale =
      1 + Math.sin(TWO_PI * params.pulseCount * progress) * params.distanceAmplitude
    const distanceScale = Math.max(minDistanceScale, requestedDistanceScale)
    const compensatedFov = calculateDollyZoomFov(input.fov, distanceScale)
    const desiredFov = THREE.MathUtils.lerp(input.fov, compensatedFov, params.fovCompensation)
    const fov = THREE.MathUtils.clamp(desiredFov, MIN_PERSPECTIVE_FOV, MAX_PERSPECTIVE_FOV)

    return {
      yaw: totalYaw * progress,
      pitch: 0,
      distanceScale,
      fov,
      clamped: distanceScale !== requestedDistanceScale || fov !== desiredFov,
    }
  })
}

function calculateDollyZoomFov(baseFov: number, distanceScale: number): number {
  const baseTangent = Math.tan(THREE.MathUtils.degToRad(baseFov) * 0.5)
  return THREE.MathUtils.radToDeg(2 * Math.atan(baseTangent / distanceScale))
}

function createButterflyKeyframes(
  input: NormalizedCameraMotionInput,
  params: Required<SemanticCameraMotionButterflyParams>
): { keyframes: SemanticCameraMotionKeyframe[]; clamped: boolean } {
  const directionSign = getOrbitDirectionSign(params.direction)
  const pitchAmplitude = THREE.MathUtils.degToRad(params.pitchAmplitude)
  const yawAmplitude = THREE.MathUtils.degToRad(params.yawAmplitude)

  return createSphericalMotionKeyframes(input, params.keyframeCount, (progress) => {
    const phase = TWO_PI * params.wingCount * progress
    return {
      yaw: Math.sin(phase) * yawAmplitude * directionSign,
      pitch: Math.sin(phase * 2) * pitchAmplitude,
    }
  })
}

function createTurbulenceKeyframes(
  input: NormalizedCameraMotionInput,
  params: Required<SemanticCameraMotionTurbulenceParams>
): { keyframes: SemanticCameraMotionKeyframe[]; clamped: boolean } {
  const directionSign = getOrbitDirectionSign(params.direction)
  const totalYaw = THREE.MathUtils.degToRad(params.angle)
  const pitchAmplitude = THREE.MathUtils.degToRad(params.pitchAmplitude)
  const yawAmplitude = THREE.MathUtils.degToRad(params.yawAmplitude)

  return createSphericalMotionKeyframes(input, params.keyframeCount, (progress) => {
    const phase = TWO_PI * progress
    const harmonicPhase = phase * params.harmonicCount
    return {
      yaw: (totalYaw * progress + Math.sin(harmonicPhase) * yawAmplitude) * directionSign,
      pitch:
        Math.sin(harmonicPhase + TURBULENCE_PITCH_PHASE_MODULATION * Math.sin(phase)) *
        pitchAmplitude,
      distanceScale: 1 + Math.sin(harmonicPhase) * params.distanceAmplitude,
    }
  })
}

function createDollyRollKeyframes(
  input: NormalizedCameraMotionInput,
  params: Required<SemanticCameraMotionDollyRollParams>
): { keyframes: SemanticCameraMotionKeyframe[]; clamped: boolean } {
  const rollSign = params.rollDirection === 'counterclockwise' ? 1 : -1

  return createSphericalMotionKeyframes(input, params.keyframeCount, (progress) => {
    const easedProgress = progress * progress * (3 - 2 * progress)
    return {
      yaw: 0,
      pitch: 0,
      distanceScale: THREE.MathUtils.lerp(1, params.distanceScale, easedProgress),
      roll: input.roll + rollSign * TWO_PI * params.rollTurns * easedProgress,
    }
  })
}

function getSignedYaw(direction: SemanticCameraMotionOrbitDirection, angle: number): number {
  return THREE.MathUtils.degToRad(angle) * getOrbitDirectionSign(direction)
}

function getOrbitDirectionSign(direction: SemanticCameraMotionOrbitDirection): number {
  return direction === 'left' ? 1 : -1
}

function createSphericalMotionKeyframes(
  input: NormalizedCameraMotionInput,
  count: number,
  sampleAt: (progress: number) => SphericalMotionSample
): { keyframes: SemanticCameraMotionKeyframe[]; clamped: boolean } {
  const offset = input.position.clone().sub(input.target)
  const yawAxis = WORLD_UP_AXIS
  const minDistance = getMinDistance(input.distance)
  const keyframes: SemanticCameraMotionKeyframe[] = []
  let clamped = false

  for (let index = 0; index < count; index++) {
    const progress = index / (count - 1)
    const sample = sampleAt(progress)
    const unclampedDistance = input.distance * (sample.distanceScale ?? 1)
    const distance = Math.max(minDistance, unclampedDistance)
    const position =
      index === 0
        ? input.position.clone()
        : createSphericalMotionPosition(input.target, offset, yawAxis, sample, distance)
    const fov = index === 0 ? input.fov : (sample.fov ?? input.fov)
    const roll = index === 0 ? input.roll : (sample.roll ?? input.roll)

    clamped = clamped || distance !== unclampedDistance || sample.clamped === true
    keyframes.push(
      createKeyframe(input.duration * progress, position, input, {
        fov,
        roll,
      })
    )
  }

  return { keyframes, clamped }
}

function createSphericalMotionPosition(
  target: THREE.Vector3,
  initialOffset: THREE.Vector3,
  yawAxis: THREE.Vector3,
  sample: SphericalMotionSample,
  distance: number
): THREE.Vector3 {
  const yawedOffset = initialOffset.clone().applyAxisAngle(yawAxis, sample.yaw)
  const pitchAxis = yawedOffset.clone().cross(yawAxis)
  if (pitchAxis.lengthSq() <= EPSILON) {
    pitchAxis.copy(createFallbackOrbitAxis(yawedOffset.clone().normalize()))
  } else {
    pitchAxis.normalize()
  }

  const finalOffset = yawedOffset.applyAxisAngle(pitchAxis, sample.pitch).setLength(distance)
  return target.clone().add(finalOffset)
}

function resolveEndpointProgressEasing(
  segmentEasing: SemanticCameraMotionSegmentEasing | undefined
): AnimationEasing | undefined {
  if (!segmentEasing?.first || !segmentEasing.last) {
    return undefined
  }

  return ENDPOINT_PROGRESS_EASING_BY_PAIR.get(`${segmentEasing.first}:${segmentEasing.last}`)
}

function shouldBakeSegmentEasingIntoPath(template: SemanticCameraMotionLeafTemplate): boolean {
  return (
    template.semantic === 'spiral' &&
    resolveEndpointProgressEasing(template.segmentEasing) !== undefined
  )
}

function applyEndpointProgressEasing(
  progress: number,
  easing: AnimationEasing | undefined
): number {
  return easing === undefined ? progress : applyAnimationEasing(progress, easing)
}

function createPerpendicularVector(axis: THREE.Vector3): THREE.Vector3 {
  const candidates = [
    new THREE.Vector3(1, 0, 0),
    new THREE.Vector3(0, 1, 0),
    new THREE.Vector3(0, 0, 1),
  ]

  for (const candidate of candidates) {
    const perpendicular = candidate.clone().addScaledVector(axis, -candidate.dot(axis))
    if (perpendicular.lengthSq() > EPSILON) {
      return perpendicular.normalize()
    }
  }

  return new THREE.Vector3(1, 0, 0)
}

function getMinDistance(distance: number): number {
  return Math.min(distance, Math.max(MIN_ABSOLUTE_DISTANCE, distance * MIN_DISTANCE_SCALE))
}

function createKeyframe(
  time: number,
  position: THREE.Vector3,
  input: NormalizedCameraMotionInput,
  overrides: { target?: THREE.Vector3; fov?: number; roll?: number } = {}
): SemanticCameraMotionKeyframe {
  return {
    time,
    position: vectorToTuple(position),
    target: vectorToTuple(overrides.target ?? input.target),
    fov: overrides.fov ?? input.fov,
    roll: overrides.roll ?? input.roll,
  }
}

function vectorToTuple(value: THREE.Vector3): [number, number, number] {
  return [value.x, value.y, value.z]
}

function appendHoldKeyframe(
  template: SemanticCameraMotionLeafTemplate,
  keyframes: SemanticCameraMotionKeyframe[],
  motionDuration: number
): SemanticCameraMotionKeyframe[] {
  const holdDuration = template.holdDuration ?? 0
  if (holdDuration <= 0 || keyframes.length === 0) {
    return keyframes
  }

  const last = keyframes[keyframes.length - 1]
  return [
    ...keyframes,
    {
      time: motionDuration + holdDuration,
      position: [...last.position],
      target: [...last.target],
      fov: last.fov,
      roll: last.roll,
    },
  ]
}

function getKeyframesDuration(keyframes: readonly SemanticCameraMotionKeyframe[]): number {
  return keyframes[keyframes.length - 1]?.time ?? 0
}

function createMotionClip(
  template: SemanticCameraMotionLeafTemplate,
  keyframes: SemanticCameraMotionKeyframe[],
  duration: number,
  motionSegmentCount: number
): AnimationClipData {
  const target: CameraAnimationTargetRef = { kind: 'camera', active: 'bind-on-play' }
  const times = keyframes.map((frame) => frame.time)
  const timing = createTrackTiming(template, Math.max(0, times.length - 1), motionSegmentCount)

  return {
    name: template.name,
    duration,
    loopMode: 'once',
    metadata: {
      semanticCameraMotion: true,
      semantic: template.semantic,
      templateId: template.id,
    },
    tracks: [
      createTrack(
        target,
        'position',
        'vec3',
        times,
        keyframes.flatMap((frame) => frame.position),
        timing
      ),
      createTrack(
        target,
        'target',
        'vec3',
        times,
        keyframes.flatMap((frame) => frame.target),
        timing
      ),
      createTrack(
        target,
        'fov',
        'number',
        times,
        keyframes.map((frame) => frame.fov),
        timing
      ),
      createTrack(
        target,
        'roll',
        'number',
        times,
        keyframes.map((frame) => frame.roll),
        timing
      ),
    ],
  }
}

function createTrackTiming(
  template: SemanticCameraMotionLeafTemplate,
  totalSegmentCount: number,
  motionSegmentCount: number
): Pick<AnimationTrackData, 'interpolation' | 'easing' | 'segmentEasing' | 'smoothness'> {
  return {
    interpolation: template.interpolation,
    ...(template.easing !== undefined ? { easing: template.easing } : {}),
    ...createSegmentEasingTiming(template, totalSegmentCount, motionSegmentCount),
    ...(template.interpolation === 'spline' ? { smoothness: 1 } : {}),
  }
}

function createSegmentEasingTiming(
  template: SemanticCameraMotionLeafTemplate,
  totalSegmentCount: number,
  motionSegmentCount: number
): Pick<AnimationTrackData, 'segmentEasing'> {
  if (
    template.segmentEasing === undefined ||
    totalSegmentCount <= 0 ||
    motionSegmentCount <= 0 ||
    shouldBakeSegmentEasingIntoPath(template)
  ) {
    return {}
  }

  const fallbackEasing = template.easing ?? 'linear'
  const segmentEasing = new Array<AnimationEasing>(totalSegmentCount).fill(fallbackEasing)
  const firstIndex = 0
  const lastIndex = motionSegmentCount - 1
  if (template.segmentEasing.first !== undefined) {
    segmentEasing[firstIndex] = template.segmentEasing.first
  }
  if (
    template.segmentEasing.last !== undefined &&
    (lastIndex !== firstIndex || template.segmentEasing.first === undefined)
  ) {
    segmentEasing[lastIndex] = template.segmentEasing.last
  }

  return { segmentEasing }
}

function createTrack(
  target: CameraAnimationTargetRef,
  propertyPath: string,
  valueType: AnimationTrackData['valueType'],
  times: number[],
  values: number[],
  timing: Pick<AnimationTrackData, 'interpolation' | 'easing' | 'segmentEasing' | 'smoothness'>
): AnimationTrackData {
  return {
    target,
    propertyPath,
    valueType,
    times,
    values,
    ...timing,
  }
}
